/**
 * The session record kept in `localStorage`, shared between the API client
 * (which reads the access token to attach to outgoing requests, and the
 * refresh token to renew it when one expires) and the auth feature (which
 * owns what a session means and when one starts or ends). Living here rather
 * than in `features/auth` is what lets `lib/api/client.ts` reach it without
 * reaching into a feature.
 *
 * `localStorage` rather than a cookie because nothing here is sent to the
 * server on its own — the client attaches the token by hand on every
 * request — and rather than `sessionStorage` so a second tab is not a second
 * sign-in.
 */
export interface StoredSession {
  admin_id: number;
  email: string;
  /**
   * Who the session belongs to, as the verify-otp answer named them.
   *
   * Kept here because there is nowhere else to get it: the backoffice API has
   * no route that describes an admin, and the one moment it says who they are
   * is the answer that starts the session. Throwing it away meant the rail had
   * only an email address to print — see `useAdmin`.
   *
   * Optional, and it has to be: a session stored before this field existed is
   * still a valid session, and the panel reads it fine without a name. It
   * fills in the next time that admin signs in.
   */
  name?: string;
  access_token: string;
  refresh_token: string;
}

const SESSION_KEY = "naurist_admin_session";

export const readStoredSession = (): StoredSession | null => {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as StoredSession;
  } catch {
    // Someone edited it, or a half-written value survived a crash. Either
    // way it is not a session, and leaving it there would fail every read.
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
};

export const writeStoredSession = (session: StoredSession): void => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
};

export const clearStoredSession = (): void => {
  localStorage.removeItem(SESSION_KEY);
};
